<template>
    <button
        type="button"
        class="btn btn--theme-dark-outline favorite-button"
        :class="isLoading && 'is-loading'"
        :disabled="isLoading"
        @click="handleClick"
        :aria-label="isFavorited ? 'Remove from favorites' : 'Add to favorites'"
        :aria-pressed="isFavorited"
        v-if="productionSeasons.length"
        v-cloak
    >
        <span class="favorite-button__icon" :class="isFavorited && 'favorite-button__icon--filled'">
            <svg viewBox="0 0 19 18" xmlns="http://www.w3.org/2000/svg" class="o-icon o-icon--heart-filled">
                <path d="M9.5 17.1768L8.39625 16.1845C6.73858 14.6807 5.36775 13.3884 4.28375 12.3078C3.19975 11.2269 2.34075 10.265 1.70675 9.422C1.07275 8.57917 0.629833 7.81025 0.378 7.11525C0.126 6.42042 0 5.71533 0 5C0 3.58083 0.4785 2.39267 1.4355 1.4355C2.39267 0.4785 3.58083 0 5 0C5.873 0 6.698 0.204167 7.475 0.6125C8.252 1.02083 8.927 1.60642 9.5 2.36925C10.073 1.60642 10.748 1.02083 11.525 0.6125C12.302 0.204167 13.127 0 14 0C15.4192 0 16.6073 0.4785 17.5645 1.4355C18.5215 2.39267 19 3.58083 19 5C19 5.71533 18.874 6.42042 18.622 7.11525C18.3702 7.81025 17.9272 8.57917 17.2932 9.422C16.6592 10.265 15.8018 11.2269 14.721 12.3078C13.6403 13.3884 12.2679 14.6807 10.6038 16.1845L9.5 17.1768ZM9.5 15.15C11.1 13.7103 12.4167 12.4763 13.45 11.448C14.4833 10.4198 15.3 9.52658 15.9 8.76825C16.5 8.00992 16.9167 7.3365 17.15 6.748C17.3833 6.15967 17.5 5.577 17.5 5C17.5 4 17.1667 3.16667 16.5 2.5C15.8333 1.83333 15 1.5 14 1.5C13.2103 1.5 12.4805 1.724 11.8105 2.172C11.1407 2.62017 10.6102 3.24358 10.2192 4.04225H8.78075C8.38325 3.23708 7.85117 2.61208 7.1845 2.16725C6.51783 1.72242 5.78967 1.5 5 1.5C4.00633 1.5 3.17458 1.83333 2.50475 2.5C1.83492 3.16667 1.5 4 1.5 5C1.5 5.577 1.61667 6.15967 1.85 6.748C2.08333 7.3365 2.5 8.00992 3.1 8.76825C3.7 9.52658 4.51667 10.4183 5.55 11.4433C6.58333 12.4683 7.9 13.7038 9.5 15.15Z" fill="#fff" />
                <path d="M9.5 15.15C11.1 13.7103 12.4167 12.4763 13.45 11.448C14.4833 10.4198 15.3 9.52658 15.9 8.76825C16.5 8.00992 16.9167 7.3365 17.15 6.748C17.3833 6.15967 17.5 5.577 17.5 5C17.5 4 17.1667 3.16667 16.5 2.5C15.8333 1.83333 15 1.5 14 1.5C13.2103 1.5 12.4805 1.724 11.8105 2.172C11.1407 2.62017 10.6102 3.24358 10.2192 4.04225H8.78075C8.38325 3.23708 7.85117 2.61208 7.1845 2.16725C6.51783 1.72242 5.78967 1.5 5 1.5C4.00633 1.5 3.17458 1.83333 2.50475 2.5C1.83492 3.16667 1.5 4 1.5 5C1.5 5.577 1.61667 6.15967 1.85 6.748C2.08333 7.3365 2.5 8.00992 3.1 8.76825C3.7 9.52658 4.51667 10.4183 5.55 11.4433C6.58333 12.4683 7.9 13.7038 9.5 15.15Z"/>
            </svg>
        </span>
    </button>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';

import { getCookie } from '../utils/cookie-handler';

const FAVORITES_COOKIE = 'favorites';

const boDomain = window.AppConfig.boDomain.replace(/^\/|\/$/g, '');
// const boDomain = 'https://tickets.tco.devspace.net';

const { data } = defineProps({
    data: {
        type: Object,
        default: null,
        required: false,
    },
});

const productionSeasons = ref([]);
const favorites = ref([]);
const isLoading = ref(true);

const isFavorited = computed(() => {
    return productionSeasons.value.some(id => {
        return favorites.value.indexOf( parseInt(id, 10) ) !== -1;
    });
});

const updateFavorites = () => {
    const cookie = getCookie(FAVORITES_COOKIE);

    let value = [];

    try {
        value = JSON.parse(cookie).pNos.map(no => {
            return parseInt(no, 10);
        });
    } catch (e) {
        // do nothing
    }

    favorites.value = value;
}

const getProductionSeasons = () => {
    if (!data || typeof data !== 'object') {
        return [];
    }

    let ids = [];

    if (Array.isArray(data.productionSeasonIds)) {
        ids = data.productionSeasonIds;
    } else if (data.productionSeasonId != null) {
        ids = [ data.productionSeasonId ];
    }

    return ids.map(id => {
        return parseInt(id, 10);
    }).filter(id => id);
}

const addToFavorites = () => {
    const promises = productionSeasons.value.map(id => {
        const url = `${boDomain}/api/favorites/add/${id}`;

        const headers = {}

        if (url.indexOf('.devspace.net') !== -1) {
            headers.Authorization = `Basic ${btoa('preview:preview')}`;
        }

        return fetch(url, {
            method: "POST",
            headers,
            credentials: "include",
        });
    });

    return Promise.all(promises).then(() => {
        updateFavorites();
    });
}

const removeFromFavorites = () => {
    const promises = productionSeasons.value.map(id => {
        const url = `${boDomain}/api/favorites/remove/${id}`;

        const headers = {}

        if (url.indexOf('.devspace.net') !== -1) {
            headers.Authorization = `Basic ${btoa('preview:preview')}`;
        }

        return fetch(url, {
            method: "POST",
            headers,
            credentials: "include",
        });
    });

    return Promise.all(promises).then(() => {
        updateFavorites();
    });
}

const handleClick = async () => {
    isLoading.value = true;

    if (isFavorited.value) {
        await removeFromFavorites();
    } else {
        await addToFavorites();
    }

    isLoading.value = false;
}

onMounted(() => {
    updateFavorites();

    productionSeasons.value = getProductionSeasons();

    isLoading.value = false;
});
</script>
